'use client'
import { getCookies } from '@/libs/cookies'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { saveActionLog, updateFacebookCookie } from '../services'
import {
  buildReferralData,
  buildReferralDataFromStored,
  cleanupReferralData,
  clearStoredReferralData,
  getFacebookCookieFromStorage,
  getReferralDataFromStorage,
  getStoredReferralData,
  hasValidReferralData,
  isFacebookCookieChanged,
  saveFacebookCookieToStorage,
  saveReferralDataToStorage,
  shouldLogAction,
} from '../utils'

// ==================== Facebook Cookie Tracker ====================

interface IUseFacebookCookieTrackerOptions {
  debounceTime?: number
  enabled?: boolean
}

export const useFacebookCookieTracker = (options?: IUseFacebookCookieTrackerOptions) => {
  const { debounceTime = 2000, enabled = true } = options || {}

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isFirstRender = useRef(true)

  /**
   * Lấy Facebook cookie hiện tại từ browser cookies
   */
  const getCurrentFacebookCookie = useCallback(async (): Promise<IFacebookCookie> => {
    return {
      _fbp: (await getCookies('_fbp')) || null,
      _fbc: (await getCookies('_fbc')) || null,
    }
  }, [])

  /**
   * Update Facebook cookie lên server nếu có thay đổi
   */
  const checkAndUpdateFacebookCookie = useCallback(async () => {
    try {
      const currentCookie = await getCurrentFacebookCookie()

      // Kiểm tra có đủ thông tin không
      if (!currentCookie._fbp || !currentCookie._fbc) {
        console.warn('[FB Cookie Tracker] Missing _fbp or _fbc cookie')
        return
      }

      // Lấy cookie đã lưu từ localStorage
      const storedCookie = getFacebookCookieFromStorage()

      // Kiểm tra có thay đổi không
      if (!isFacebookCookieChanged(storedCookie, currentCookie)) {
        console.log('[FB Cookie Tracker] No changes detected, skipping API call')
        return
      }

      console.log('[FB Cookie Tracker] Changes detected, updating...', {
        stored: storedCookie,
        current: currentCookie,
      })

      // Call API để update
      const response = await updateFacebookCookie(currentCookie)

      if (response?.status === 'success') {
        // Lưu vào localStorage sau khi update thành công
        saveFacebookCookieToStorage(currentCookie)
        console.log('[FB Cookie Tracker] Updated successfully')
      } else {
        console.error('[FB Cookie Tracker] Update failed:', response?.message)
      }
    } catch (error) {
      console.error('[FB Cookie Tracker] Error:', error)
    }
  }, [getCurrentFacebookCookie])

  /**
   * Check Facebook cookie với debounce
   */
  const debouncedCheck = useCallback(() => {
    // Clear timeout cũ nếu có
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set timeout mới
    debounceTimerRef.current = setTimeout(() => {
      checkAndUpdateFacebookCookie()
    }, debounceTime)
  }, [checkAndUpdateFacebookCookie, debounceTime])

  useEffect(() => {
    // Không chạy nếu disabled
    if (!enabled) {
      console.log('[FB Cookie Tracker] Disabled')
      return
    }

    // Skip first render để tránh call API ngay lập tức
    if (isFirstRender.current) {
      isFirstRender.current = false
      // Nhưng vẫn check sau debounceTime
      debouncedCheck()
      return
    }

    // Check khi có sự thay đổi
    debouncedCheck()

    // Cleanup timeout khi unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [enabled, debouncedCheck])

  return {
    checkAndUpdateFacebookCookie,
  }
}

// ==================== Referral Params Capture ====================

interface IUseCaptureReferralParamsOptions {
  /**
   * Hàm determine referral type từ auth utils
   */
  determineReferralTypeFn: (params: IDetermineReferralTypeParams) => ReferralType
  /**
   * Enable/disable capturing
   */
  enabled?: boolean
  /**
   * Callback khi capture thành công
   */
  onCaptured?: (data: IStoredReferralData) => void
}

/**
 * Hook để capture referral params từ URL ngay khi user vào trang
 * Tự động parse URL params và lưu vào localStorage
 */
export const useCaptureReferralParams = (options: IUseCaptureReferralParamsOptions) => {
  const { determineReferralTypeFn, enabled = true, onCaptured } = options
  const [isCaptured, setIsCaptured] = useState(false)
  const hasCapturedRef = useRef(false)

  useEffect(() => {
    if (!enabled) return
    if (hasCapturedRef.current) return // Chỉ capture 1 lần
    if (typeof window === 'undefined') return

    try {
      // Lấy referralSource từ localStorage (nếu có)
      const referralSource = localStorage.getItem('referralSource')

      // Lấy targetUrl từ current URL
      const targetUrl = window.location.href

      // Lấy mailing_id từ URL params
      const urlParams = new URLSearchParams(window.location.search)
      const mailingId = urlParams.get('mailing_id') || ''

      // Check nếu có referral params
      const hasReferralParams =
        urlParams.get('utm_source') ||
        urlParams.get('utm_medium') ||
        urlParams.get('utm_campaign') ||
        urlParams.get('ref') ||
        urlParams.get('fbclid') ||
        urlParams.get('gclid') ||
        urlParams.get('camp') ||
        urlParams.get('adsid') ||
        urlParams.get('platform') ||
        mailingId ||
        referralSource

      if (hasReferralParams) {
        console.log('[Referral Capture] Detected referral params')

        // Save vào localStorage
        saveReferralDataToStorage(referralSource, targetUrl, mailingId, determineReferralTypeFn)

        // Get stored data to trigger callback
        const storedData = getStoredReferralData()
        if (storedData) {
          setIsCaptured(true)
          hasCapturedRef.current = true
          onCaptured?.(storedData)
          console.log('[Referral Capture] Saved to storage:', storedData)
        }
      } else {
        console.log('[Referral Capture] No referral params detected')
      }
    } catch (error) {
      console.error('[Referral Capture] Error:', error)
    }
  }, [enabled, determineReferralTypeFn, onCaptured])

  return {
    isCaptured,
    hasValidData: hasValidReferralData(),
  }
}

// ==================== Referral Tracking ====================

interface IUseReferralTrackingOptions {
  /**
   * User đã đăng nhập chưa
   */
  isLoggedIn: boolean
  /**
   * Hàm determine referral type từ auth utils
   */
  determineReferralTypeFn: (params: IDetermineReferralTypeParams) => ReferralType
  /**
   * Enable/disable tracking
   */
  enabled?: boolean
}

/**
 * Hook để track và log referral source khi user login
 *
 * Workflow:
 * 1. Check referral data có sẵn không (new format hoặc old format)
 * 2. Khi user login → log action returning với referral data
 * 3. Sau khi log thành công → clear localStorage
 * 4. Chỉ log 1 lần duy nhất per session
 */
export const useReferralTracking = (options: IUseReferralTrackingOptions) => {
  const { isLoggedIn, determineReferralTypeFn, enabled = true } = options

  const hasLoggedRef = useRef(false)
  const [isReady, setIsReady] = useState(false)

  /**
   * Mutation để log action returning
   */
  const { mutate: logAction, isPending } = useMutation({
    mutationFn: async (referralData: string) => {
      // Skip nếu là direct visit và chưa qua 1 giờ
      if (referralData?.includes('direct')) {
        const lastAction = localStorage.getItem('lastAction')
        if (!shouldLogAction(lastAction, 3600000)) return null

        localStorage.removeItem('close_special_program')
      }

      return await saveActionLog({
        type: 'returning',
        title: 'Quay lại',
        additional_data: referralData,
      })
    },
    onSuccess: (data) => {
      if (!data) return

      localStorage.setItem('lastAction', Date.now().toString())

      if (data?.data) {
        // Delay để đảm bảo data được sử dụng xong
        setTimeout(() => {
          clearStoredReferralData()
          cleanupReferralData()
        }, 1000)
      }
    },
  })

  /**
   * Check referral data ready
   */
  useEffect(() => {
    if (!enabled) return

    const timer = setTimeout(() => {
      const hasNewData = hasValidReferralData()
      const { referralSource, targetUrl, mailId } = getReferralDataFromStorage()
      const hasOldData = !!(referralSource || targetUrl || mailId)

      setIsReady(hasNewData || hasOldData)
    }, 100)

    return () => clearTimeout(timer)
  }, [enabled])

  /**
   * Log referral khi user login
   */
  useEffect(() => {
    if (!enabled || !isLoggedIn || !isReady || isPending) return
    if (hasLoggedRef.current) return
    if (localStorage.getItem('sal')) return // Đã save action login

    // Lấy và build referral data
    const storedData = getStoredReferralData()
    const { referralSource, targetUrl, mailId } = getReferralDataFromStorage()

    let data: string | null = null

    if (storedData && !storedData.is_sent) {
      data = buildReferralDataFromStored(storedData)
    } else if (referralSource || targetUrl || mailId) {
      data = buildReferralData(referralSource, targetUrl, mailId, determineReferralTypeFn)
    }

    if (data) {
      hasLoggedRef.current = true
      logAction(data)
    }
  }, [enabled, isLoggedIn, isReady, isPending, determineReferralTypeFn, logAction])

  /**
   * Reset flag khi logout
   */
  useEffect(() => {
    if (!isLoggedIn) {
      hasLoggedRef.current = false
    }
  }, [isLoggedIn])

  /**
   * Cleanup sal flag
   */
  useEffect(() => {
    const sal = localStorage.getItem('sal')
    if (sal) {
      const timer = setTimeout(() => localStorage.removeItem('sal'), 2000)
      return () => clearTimeout(timer)
    }
  }, [])

  return {
    isReady,
    isPending,
    logAction,
  }
}
